import React, {useState, useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Helmet} from 'react-helmet';

// SELECTORS
import {
  isAltAccoSelector,
  galleryPopupTypeSelector,
  hotelNameSelector,
  mediaV2Selector,
  showVideoDwebSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';

// CONFIG
import {omnitureConfig, omnitureThunk} from '@stencil/omniture-analytics';
import {categoryTypes} from '@stencil/hotels-config/src/constants/details';

// TYPES
import {
  DetailsPageComponents,
  detailsClick,
} from '@stencil/omniture-analytics/src/constants/details';

// ACTIONS
import {updateGalleryPopupType} from '@stencil/hotels-cg-reducer/src/details/actions';

// HOOKS
import useOmnitureVisibleCall from '@stencil/omniture-analytics/src/hooks/useOmnitureVisibleCall';

// COMPONENTS
import Modal, {useModal} from '@stencil/hotels-modal-component';
import AmenityChips from '../PriceNBookingButtonNPersuasion/AmenityChips';
import CaptivateBannerGalleryWithVideo from '../../../../../stories/components/hotels/DetailsPageUnif/CaptivateImagesGrid/CaptivateBannerGalleryWithVideo/CaptivateBannerGalleryWithVideo';
import ImageCategory from '../../../../../stories/components/hotels/DetailsPageUnif/CaptivateImagesGrid/CaptivateBannerGalleryWithVideo/ImageCategory';
import CaptivateBannerGallery from '../../../../../stories/components/hotels/DetailsPageUnif/CaptivateImagesGrid/CaptivateBannerGallery/CaptivateBannerGallery';
import CaptivateGalleryModalChunk from '../../../../../stories/components/hotels/DetailsPageUnif/CaptivateImagesGrid/CaptivateAllImageModal/CaptivateGalleryModalChunk';

// STYLES
import * as S from './CaptivateGallery.styles';

interface Category {
  id?: string | number;
  title: string;
  count: number;
  imageAlt: string;
  imageUrl: string;
  mediaType?: string;
  onImageClick: (category?: string) => void;
  keyName?: string;
  tagName?: string;
}

const CaptivateGallery = ({isSeo = false, isSoldOut}: {isSeo?: boolean; isSoldOut: boolean}) => {
  const dispatch = useDispatch();
  const mediaV2Data = useSelector(mediaV2Selector);
  const galleryPopupType = useSelector(galleryPopupTypeSelector);
  const showDwebVideos = useSelector(showVideoDwebSelector);
  const isAltAcco = useSelector(isAltAccoSelector);
  const hotelName = useSelector(hotelNameSelector);

  const [setOmniImpressions] = useOmnitureVisibleCall();
  const [typeOfGalleryPopup, updateTypeOfGalleryPopup] = useState('');
  const [mediaDetails, setMediaDataDetails] = useState({type: '', selectedTagId: ''});
  const [carouselState, updateCarouselState] = useState({isOpen: false, imageNo: ''});

  const recordOmnitureScrolls = useCallback(
    (componentAdd: string) => {
      setOmniImpressions((prevState: string[]) => [...prevState, componentAdd]);
    },
    [setOmniImpressions],
  );

  const setTypeOfGalleryPopup = useCallback(
    (type: string) => {
      dispatch(updateGalleryPopupType(type));
    },
    [dispatch],
  );

  const changeTypeOfGalleryPopup = useCallback(
    (type: string, openVideo?: boolean, isViewAll = false) => {
      updateTypeOfGalleryPopup(type);
      setTypeOfGalleryPopup(type);

      let omniName = detailsClick.captivateGalleryV2.Property_photos_tab_clicked;
      if (type === detailsClick.captivateGalleryV2.tabs.traveller) {
        omniName = detailsClick.captivateGalleryV2.Guest_photos_tab_clicked;
      } else if (isViewAll) {
        omniName = detailsClick.captivateGalleryV2.Property_photos_tab_clicked;
      }

      dispatch(
        omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
          cta: {
            name: omniName,
            type: omnitureConfig.ctaType.Card,
            componentName: DetailsPageComponents.GalleryV2,
            destination: '',
          },
        }),
      );
    },
    [updateTypeOfGalleryPopup, setTypeOfGalleryPopup, dispatch],
  );

  const updateMediaDetails = useCallback(
    (type: string, selectedTagId: string) => {
      setMediaDataDetails({type, selectedTagId});
      if (carouselState.isOpen) {
        updateCarouselState({isOpen: false, imageNo: ''});
      }
    },
    [carouselState],
  );

  const openCaptivateGalleryModal = (
    typeOfPopup: string,
    openVideo?: boolean,
    tagName?: string,
  ) => {
    window.scrollTo({top: 86, behavior: 'smooth'});
    openGalleryModal();
    updateMediaDetails(tagName || categoryTypes.allMedia);
    changeTypeOfGalleryPopup(typeOfPopup, openVideo);
    if (carouselState.isOpen) {
      updateCarouselState({isOpen: false, imageNo: ''});
    }
  };

  const onImageCategoryClick = useCallback((category = 'hotellier', tagName) => {
    openCaptivateGalleryModal(category, false, tagName);
  }, []);

  useEffect(() => {
    updateTypeOfGalleryPopup(galleryPopupType);
    updateCarouselState({isOpen: false, imageNo: ''});
  }, [galleryPopupType]);

  const {
    isModalOpen: isGalleryModalOpen,
    closeModal: closeGalleryModal,
    openModal: openGalleryModal,
  } = useModal();

  const getTotalImagesCount = data => {
    return (
      data?.tags?.reduce((acc: number, tag: any) => acc + (tag.subtags[0]?.data?.length || 0), 0) ||
      0
    );
  };

  const getFirstMedia = data => {
    return data?.tags?.[0]?.subtags?.[0]?.data?.[0];
  };

  const {
    categories,
    totalHoteilerImagesCount,
    totalTravellerImagesCount,
    galleryImages,
    hotelFirstImage,
  } = useMemo(() => {
    const categories: Category[] = [];
    const hotelierData = mediaV2Data?.hotel || {};
    const travellerData = mediaV2Data?.traveller || {};
    const galleryImages = mediaV2Data?.grid?.images || [];
    const hotelFirstImage = galleryImages?.[0]?.url || '';

    const hotelierFirstMedia = getFirstMedia(hotelierData);
    const travellerFirstMedia = getFirstMedia(travellerData);

    const totalHoteilerImagesCount = getTotalImagesCount(hotelierData);
    const totalTravellerImagesCount = getTotalImagesCount(travellerData);

    if (hotelierFirstMedia?.url) {
      const firstTag = hotelierData?.tags?.[0] || {};
      const isVideo = hotelierFirstMedia.mediaType === 'VIDEO';
      categories.push({
        id: 0,
        mediaType: hotelierFirstMedia.mediaType,
        title: firstTag?.name,
        count: firstTag?.subtags?.[0]?.data?.length,
        imageAlt: `${hotelName} - Tap to explore photos`,
        imageUrl: hotelierFirstMedia[isVideo ? 'thumbnailURL' : 'url'],
        onImageClick: onImageCategoryClick,
        keyName: 'hotellier',
        tagName: firstTag?.name,
      });
    }
    if (travellerFirstMedia?.url) {
      const isVideo = travellerFirstMedia.mediaType === 'VIDEO';
      categories.push({
        title: 'Traveller Photos',
        count: totalTravellerImagesCount,
        imageAlt: `${hotelName} - Traveller Photo`,
        imageUrl: travellerFirstMedia[isVideo ? 'thumbnailURL' : 'url'],
        onImageClick: onImageCategoryClick,
        keyName: 'traveller',
        tagName: '',
      });
    } else if (hotelierData?.tags?.length > 1) {
      const secondTag = hotelierData?.tags?.[1] || {};
      const hotelierSecondMedia = secondTag?.subtags?.[0].data?.[0] || {};
      if (hotelierSecondMedia?.url) {
        categories.push({
          id: 0,
          mediaType: hotelierSecondMedia.mediaType,
          title: secondTag?.name,
          count: secondTag?.subtags?.[0]?.data?.length,
          imageAlt: `${hotelName} - Tap to explore photos`,
          imageUrl: hotelierSecondMedia.url,
          onImageClick: onImageCategoryClick,
          keyName: 'hotellier',
          tagName: secondTag?.name,
        });
      }
    }
    return {
      categories,
      totalHoteilerImagesCount,
      totalTravellerImagesCount,
      galleryImages,
      hotelFirstImage,
    };
  }, [hotelName, onImageCategoryClick, mediaV2Data]);

  return (
    <S.ImageGalleryWrapper isSeo={isSeo}>
      <Helmet>
        {isSeo && hotelFirstImage && <link rel="preload" as="image" href={hotelFirstImage} />}
      </Helmet>
      <Modal
        isOpen={isGalleryModalOpen}
        closeModal={closeGalleryModal}
        modalStyles={{
          width: '120rem',
          background: 'none',
          position: 'fixed',
          transform: 'translateX(-50%)',
          top: isAltAcco ? '8.6rem' : '17.7rem',
        }}
        overlayProps={{
          background: isAltAcco
            ? 'linear-gradient(rgba(0, 0, 0, 0.4) 8.6rem, #141823 9%)'
            : 'rgba(0,0,0,0.88)',
        }}
        hideCloseButton={true}
      >
        <S.Backdrop onClick={closeGalleryModal}></S.Backdrop>

        <CaptivateGalleryModalChunk
          hotelName={hotelName}
          onClose={closeGalleryModal}
          typeOfGalleryPopup={typeOfGalleryPopup}
          updateTypeOfGalleryPopup={openCaptivateGalleryModal}
          pokusShowDwebVideo={showDwebVideos}
          mediaV2Data={mediaV2Data}
          carouselState={carouselState}
          updateCarouselState={updateCarouselState}
          mediaDetails={mediaDetails}
          updateMediaDetails={updateMediaDetails}
          recordOmnitureScrolls={recordOmnitureScrolls}
        />
      </Modal>
      {isAltAcco ? (
        <S.AltAccoCaptivateBannerContainer>
          <CaptivateBannerGallery
            openCaptivateGalleryModal={openCaptivateGalleryModal}
            captivateImages={galleryImages}
            hotellierImagesCount={totalHoteilerImagesCount}
            travellerImagesCount={totalTravellerImagesCount}
          />
          {isSoldOut ? (
            <ImageCategory
              categories={categories}
              isSeoPage={isSeo}
              customStyles={{imageHeight: '152px'}}
            />
          ) : (
            <AmenityChips />
          )}
        </S.AltAccoCaptivateBannerContainer>
      ) : (
        <CaptivateBannerGalleryWithVideo
          isSeoPage={isSeo}
          hotelName={hotelName}
          captivateImages={galleryImages}
          hotellierImagesCount={totalHoteilerImagesCount}
          travellerImagesCount={totalTravellerImagesCount}
          onHotelierImageClick={onImageCategoryClick}
          categories={categories}
        />
      )}
    </S.ImageGalleryWrapper>
  );
};

export default CaptivateGallery;
